import { useState } from 'react';
import { GestureResponderEvent, Pressable } from 'react-native';
import { Menu } from 'react-native-paper';
import { Icon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export interface CallActor {
  id: string;
  name: string;
}

interface CallActorMenuProps {
  actor: CallActor;
  onCall: (actor: CallActor) => void;
}

/**
 * Per-row call affordance: a video-call icon that opens a small menu with a
 * "Call" action for the row's actor. Lives in the activity card's trailing
 * slot. Stops press propagation so tapping it never navigates into the row.
 */
export const CallActorMenu = ({ actor, onCall }: CallActorMenuProps) => {
  const [visible, setVisible] = useState(false);

  const open = (e: GestureResponderEvent) => {
    e?.stopPropagation?.();
    setVisible(true);
  };

  const call = () => {
    setVisible(false);
    onCall(actor);
  };

  return (
    <Menu
      visible={visible}
      onDismiss={() => setVisible(false)}
      contentStyle={styles.menu}
      anchor={
        <Pressable
          onPress={open}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={`More options for ${actor.name}`}
          style={styles.trigger}
        >
          <Icon name="Video" size={18} color={Colors.role.textPrimary} />
        </Pressable>
      }
    >
      <Menu.Item
        leadingIcon={() => (
          <Icon name="Video" size={18} color={Colors.role.textStrong} />
        )}
        onPress={call}
        title={`Call ${actor.name}`}
      />
    </Menu>
  );
};

const styles = themedStyleSheet((role) => ({
  trigger: {
    padding: tokens.spacing.xs,
    borderRadius: tokens.borderRadius.sm,
  },
  menu: {
    backgroundColor: role.canvas,
  },
}));
